import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RiBox3Line } from 'react-icons/ri';
import { MdInventory } from 'react-icons/md';
import { IoTrendingDown, IoAnalyticsOutline } from 'react-icons/io5';
import { IoIosReturnLeft } from 'react-icons/io';
import { FiChevronDown, FiDownload } from 'react-icons/fi';
import toast from '../components/common/toast';
import Table from '../components/common/Table';
import SkeletonTable from '../components/SkeletonTable';
import { reportService, companyService, warehouseService } from '../services/api';
import { exportPalletStorageReportPdfFromExcelBlob } from '../utils/exportPalletStorageReportPdf';
import { exportCustomerMisPalletReportPdfFromExcelBlob } from '../utils/exportCustomerMisPalletReportPdf';
import {
  exportInventoryBatchReportPdf,
  fetchAllInventoryBatchReportRows,
} from '../utils/exportInventoryBatchReportPdf';
import { exportInventoryByProductReportPdf } from '../utils/exportInventoryByProductReportPdf';
import ReportDetailsModal from '../components/common/ReportDetailsModal';
import Modal from '../components/common/Modal';
import {
 TotalProductsIcon,
 TotalBinsIcon,
 TotalProductsIcon as TotalUOMIcon, // agar alag UOM icon nahi hai to ye use kar lo
} from "../assets/states/StatsIcons";

const SummaryCard = ({
  label,
  value,
  Icon,
  bg,
  stroke = true,
  icon,
  iconBgColor = 'bg-gray-100',
  iconTextColor = 'text-gray-600',
}) => {
  const hasIconComponent = Icon && typeof Icon === 'function';
  const hasIconElement = icon != null && React.isValidElement(icon);

  const boxStyle = {
    width: 40,
    height: 40,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 20,
    borderBottomLeftRadius: 6,
    ...(hasIconComponent && bg ? { background: bg } : {}),
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm w-full min-w-0">
      <div className="flex items-center gap-5">
        <div
          className={`flex items-center justify-center flex-shrink-0 ${hasIconElement ? iconBgColor : ''}`}
          style={boxStyle}
        >
          {hasIconComponent && (stroke ? <Icon size={18} color="#FFFFFF" /> : <Icon size={24} />)}
          {hasIconElement && <span className={iconTextColor}>{icon}</span>}
        </div>
        <div className="min-w-0">
          <p className="text-xl font-semibold text-gray-900 leading-none">{value}</p>
          <p className="mt-2 text-sm font-medium text-gray-400 whitespace-nowrap truncate">
            {label}
          </p>
        </div>
      </div>
    </div>
  );
};


const REPORT_TABS = [
  { id: 'product', label: 'Inventory by Product' },
  { id: 'batch', label: 'Inventory by Batch' },
  // { id: 'uom', label: 'Inventory by UOM' },
  // { id: 'summary', label: 'Inventory by Summary' },
  { id: 'aging', label: 'Ageing Report' },
  { id: 'pallet', label: 'Pallet Reports' },
];

const PALLET_SUB_TABS = [
  { id: 'storageVisibility', label: 'Pallet Storage Visibility' },
  { id: 'customerMis', label: 'Customer MIS Pallet' },
];

const formatPalletReportNumber = (val) => {
  if (val == null || val === '') return '—';
  const n = Number(val);
  return Number.isFinite(n) ? n : '—';
};

const PALLET_STORAGE_MOVEMENT_COLUMNS = [
  { key: 'casesOpening', label: 'Opening', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'casesInward', label: 'Inward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'casesOutward', label: 'Outward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'casesClosing', label: 'Closing', align: 'right', render: (val) => formatPalletReportNumber(val) },
  {
    key: 'casesOpeningPlusInward',
    label: 'Opening + Inward',
    align: 'right',
    render: (val) => formatPalletReportNumber(val),
  },
  { key: 'palletsOpening', label: 'Opening', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'palletsInward', label: 'Inward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'palletsOutward', label: 'Outward', align: 'right', render: (val) => formatPalletReportNumber(val) },
  { key: 'palletsClosing', label: 'Closing', align: 'right', render: (val) => formatPalletReportNumber(val) },
  {
    key: 'palletsOpeningPlusInward',
    label: 'Opening + Inward',
    align: 'right',
    render: (val) => formatPalletReportNumber(val),
  },
];

const PALLET_STORAGE_COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'skuCode', label: 'SKU Code' },
  { key: 'skuName', label: 'SKU Name' },
  { key: 'uom', label: 'UOM', align: 'center' },
  { key: 'conversionQtyPerPallet', label: 'Conversion Qty Per Pallet', align: 'right' },
  { key: 'currentUnitsStock', label: 'Current Units Stock', align: 'right' },
  { key: 'currentPalletStock', label: 'Current Pallet Stock', align: 'right' },
  { key: 'weightPerPallet', label: 'Weight Per Pallet', align: 'right' },
  {
    key: 'date',
    label: 'Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  ...PALLET_STORAGE_MOVEMENT_COLUMNS,
];

const PALLET_STORAGE_GROUPED_HEADERS = {
  baseColumnCount: 9,
  groups: [
    { label: 'CASES', span: 5 },
    { label: 'PALLETS', span: 5 },
  ],
};

const PALLET_CUSTOMER_MIS_COLUMNS = [
  { key: 'customer', label: 'Customer' },
  { key: 'sku', label: 'SKU' },
  { key: 'skuName', label: 'SKU Name' },
  { key: 'receivedUnits', label: 'Received Units', align: 'right' },
  { key: 'receivedPalletEquivalent', label: 'Received Pallet Equivalent', align: 'right' },
  { key: 'currentUnitsStock', label: 'Current Units Stock', align: 'right' },
  { key: 'currentPalletStock', label: 'Current Pallet Stock', align: 'right' },
  { key: 'dispatchedUnits', label: 'Dispatched Units', align: 'right' },
  { key: 'dispatchedPalletEquivalent', label: 'Dispatched Pallet Equivalent', align: 'right' },
  { key: 'conversionQtyPerPallet', label: 'Conversion Qty Per Pallet', align: 'right' },
  { key: 'weightPerPallet', label: 'Weight Per Pallet', align: 'right' },
];

const STORAGE_TYPE_OPTIONS = [
  { value: '', label: 'All Storage Types' },
  { value: 'FROZEN', label: 'Frozen' },
  { value: 'CHILLED', label: 'Chilled' },
  { value: 'TROPICAL', label: 'Tropical' },
  { value: 'DRIED', label: 'Dried' },
];

const PRODUCT_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'SKU Name' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'uom', label: 'UOM' },
  { key: 'openingStock', label: 'Opening', align: 'right' },
  { key: 'inwardQuantity', label: 'Inward', align: 'right' },
  { key: 'outwardQuantity', label: 'Outward', align: 'right' },
  { key: 'returnQuantity', label: 'Return', align: 'right' },
  { key: 'closingStock', label: 'Closing', align: 'right' },
  // Batch Details is export-only (Inventory by Product CSV). Keep this column
  // definition commented out so it is not shown in the on-screen table.
  // {
  //   key: 'batchDetails',
  //   label: 'Batch Details',
  //   render: (val) => {
  //     if (!val || !Array.isArray(val) || !val.length) return '—';
  //     return val
  //       .map((b) => {
  //         const qty = Number(b.quantity ?? 0);
  //         const condition = String(b.condition || '').toUpperCase();
  //         const suffix = condition === 'HOLD' ? ` [HOLD]` : '';
  //         return `${b.batchNumber ?? '—'}: ${qty}${suffix}`;
  //       })
  //       .join(' | ');
  //   },
  // },
  // { key: 'availableQuantity', label: 'Available', align: 'right' },
  // { key: 'goodQuantity', label: 'GOOD', align: 'right' },
  // { key: 'holdQuantity', label: 'HOLD', align: 'right' },
];

const Pill = ({ text, variant = "blue" }) => {
 const map = {
   green: "border-emerald-500 text-emerald-600 bg-emerald-50/50",
   orange: "border-orange-300 text-orange-400 bg-orange-50/50",
   blue: "border-indigo-800 text-indigo-900 bg-indigo-50/40",
   gray: "border-gray-300 text-gray-700 bg-gray-50",
 };

 return (
   <span
     className={`inline-flex items-center justify-center rounded-full border px-4 py-1 text-sm font-medium whitespace-nowrap ${map[variant] || map.gray}`}
   >
     {text}
   </span>
 );
};

const BATCH_COLUMNS = [
 { key: 'productCode', label: 'Product Code' },
 { 
   key: 'companyName', 
   label: 'Company',
   render: (val) => val || '—'
 },
 { key: 'batchNumber', label: 'Batch Number' },
 {
   key: 'manufacturingDate',
   label: 'Manufacturing Date',
   render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
 },
 {
   key: 'expiryDate',
   label: 'Exp Date',
   render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
 },
 { key: 'quantity', label: 'Quantity', align: 'right' },

 // ✅ condition pill (Expired / Good)
 {
   key: 'condition',
   label: 'Condition',
   render: (val) => {
     const v = String(val || '').toLowerCase();
     const isExpired = v.includes('expired');
     const isGood = v.includes('good');
     return (
       <Pill
         text={val || '—'}
         variant={isExpired ? 'orange' : isGood ? 'green' : 'gray'}
       />
     );
   },
 },

 // { key: 'binCode', label: 'Bin Code' },

 // ✅ binType pill (blue)
 // {
 //   key: 'binType',
 //   label: 'Bin Type',
 //   render: (val) => <Pill text={val || '—'} variant="blue" />,
 // },
];


// const UOM_COLUMNS = [
//   { key: 'uom', label: 'UOM' },
//   { key: 'productCode', label: 'Product Code' },
//   { key: 'productDescription', label: 'Description' },
//   { 
//     key: 'companyName', 
//     label: 'Company',
//     render: (val) => val || '—'
//   },
//   { key: 'qnt', label: 'Qnt', align: 'right' },
//   { key: 'holdQty', label: 'Hold Qnt', align: 'right' },
// ];

const EXPIRED_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'manufacturingDate',
    label: 'Manufacturing Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  // { 
  //   key: 'shelfLifeRemainingPercent', 
  //   label: '% Remaining',
  //   align: 'right',
  //   render: (val) => val != null ? `${val}%` : '—'
  // },
  { key: 'quantity', label: 'Quantity', align: 'right' },
  // { key: 'binCode', label: 'Bin Code' },
];

const NEAR_EXPIRY_COLUMNS = [
  { key: 'productCode', label: 'Product Code' },
  { 
    key: 'companyName', 
    label: 'Company',
    render: (val) => val || '—'
  },
  { key: 'batchNumber', label: 'Batch Number' },
  {
    key: 'manufacturingDate',
    label: 'Manufacturing Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  {
    key: 'expiryDate',
    label: 'Exp Date',
    render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
  },
  { 
    key: 'shelfLifeRemainingPercent', 
    label: '% Remaining',
    align: 'right',
    render: (val) => val != null ? `${val}%` : '—'
  },
  { key: 'daysUntilExpiry', label: 'Days Left', align: 'right' },
  { key: 'quantity', label: 'Quantity', align: 'right' },
];

const CARD_TITLES = {
  product: 'Inventory Report (Product)',
  batch: 'Inventory Report (Batch)',
  // uom: 'Inventory Report (UOM)',
  // summary: 'Inventory Summary',
  aging: 'Ageing Report',
  pallet: 'Pallet Reports',
};

const REPORT_FILTER_PANEL_CLASS =
  'mb-6 w-full min-w-0 rounded-2xl border border-[#EEF1F5] bg-white p-4 sm:p-5';
const REPORT_FILTER_PANEL_SHADOW = { boxShadow: '0px 0px 4px 0px #959DA526' };
const REPORT_FIELD_LABEL_CLASS =
  'flex flex-col gap-1.5 text-xs font-semibold text-[#667085]';
const REPORT_FIELD_INPUT_CLASS =
  'w-full min-w-0 rounded-[12px] border border-[#D0D5DD] bg-white px-3 text-sm text-[#101828] h-11';
const REPORT_EXPORT_BTN_CLASS =
  'flex items-center justify-center gap-2 rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] font-semibold text-sm transition hover:bg-[#188742]/5 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap h-11 px-4 shrink-0';
const REPORT_CLEAR_BTN_CLASS =
  'rounded-[12px] border border-[#D0D5DD] bg-white text-[#344054] font-semibold text-sm transition hover:bg-gray-50 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap h-11 px-4 shrink-0';
const REPORT_FILTERS_BTN_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-[12px] border border-[#D0D5DD] bg-white text-[#344054] font-semibold text-sm transition hover:bg-gray-50 active:scale-[0.98] whitespace-nowrap h-11 px-4 shrink-0 max-w-full';
const REPORT_FILTERS_BTN_ACTIVE_CLASS =
  'border-[#188742] text-[#188742] bg-[#188742]/5';
const REPORT_COLLAPSIBLE_BODY_CLASS =
  'rounded-xl border border-[#EEF1F5] bg-[#FCFCFC] p-4 sm:p-5 min-w-0 overflow-hidden';

const PRODUCT_DETAIL_FIELDS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'SKU Name' },
  { key: 'companyName', label: 'Company' },
  { key: 'uom', label: 'UOM' },
  { key: 'openingStock', label: 'Opening Stock' },
  { key: 'inwardQuantity', label: 'Inward Quantity' },
  { key: 'outwardQuantity', label: 'Outward Quantity' },
  { key: 'returnQuantity', label: 'Return Quantity' },
  { key: 'closingStock', label: 'Closing Stock' },
  { key: 'availableQuantity', label: 'Available Quantity' },
  { key: 'goodQuantity', label: 'GOOD Quantity' },
  { key: 'holdQuantity', label: 'HOLD Quantity' },
  { key: 'batchDetails', label: 'Batch Details' },
  { key: 'reportStartDate', label: 'Report Start Date' },
  { key: 'reportEndDate', label: 'Report End Date' },
];

const BATCH_DETAIL_FIELDS = [
  { key: 'productCode', label: 'Product Code' },
  { key: 'productDescription', label: 'Description' },
  { key: 'skuName', label: 'SKU Name' },
  { key: 'companyName', label: 'Company' },
  { key: 'batchNumber', label: 'Batch Number' },
  { key: 'manufacturingDate', label: 'Manufacturing Date', format: 'date' },
  { key: 'expiryDate', label: 'Expiry Date', format: 'date' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'condition', label: 'Condition' },
  { key: 'binCode', label: 'Bin Code' },
  { key: 'binType', label: 'Bin Type' },
  { key: 'uom', label: 'UOM' },
  { key: 'daysUntilExpiry', label: 'Days Until Expiry' },
  { key: 'shelfLifeRemainingPercent', label: 'Shelf Life Remaining %' },
  { key: 'shelfLifeUsedPercent', label: 'Shelf Life Used %' },
];

function buildDetailFields(row, fieldDefs) {
  if (!row || typeof row !== 'object') return [];
  const seen = new Set();
  const fields = [];

  for (const def of fieldDefs) {
    if (!(def.key in row)) continue;
    seen.add(def.key);
    const value = row[def.key];
    if (def.key === 'batchDetails' && Array.isArray(value)) {
      // Render the batch list as a clear Batch Number / Quantity mapping (per-batch
      // quantity taken from the existing batch records returned by the API).
      const batchNumbers = value.map((b) => b.batchNumber || '—').join(', ');
      const uom = row.uom || '';
      const quantities = value
        .map((b) => {
          const q = b.quantity != null ? b.quantity : '—';
          return uom ? `${q} ${uom}` : String(q);
        })
        .join(', ');
      fields.push({ key: 'batchNumber', label: 'Batch Number', value: batchNumbers });
      fields.push({ key: 'batchQuantity', label: 'Batch Quantity', value: quantities });
      continue;
    }
    fields.push({
      key: def.key,
      label: def.label,
      value,
      format: def.format,
    });
  }

  // Surface any remaining simple scalar fields from the row object.
  for (const [key, value] of Object.entries(row)) {
    if (seen.has(key)) continue;
    if (value != null && typeof value === 'object') continue;
    const label = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
    fields.push({ key, label, value });
  }

  return fields;
}

export function Reports() {
  const [activeReport, setActiveReport] = useState('product');
  const [palletSubReport, setPalletSubReport] = useState('storageVisibility');
  const [palletPdfModalOpen, setPalletPdfModalOpen] = useState(false);
  const [palletPdfClientDraft, setPalletPdfClientDraft] = useState('');
  const [palletPdfClientError, setPalletPdfClientError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [page, setPage] = useState(1);
  const limit = null;
  const [batchReportPageSize, setBatchReportPageSize] = useState(20);
  const [searchFilter] = useState('');
  const [skuDraft, setSkuDraft] = useState('');
  const [binDraft, setBinDraft] = useState('');
  const [storageTypeDraft, setStorageTypeDraft] = useState('');
  const [startDateDraft, setStartDateDraft] = useState('');
  const [endDateDraft, setEndDateDraft] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    companyId: '',
    search: '',
    sku: '',
    binCode: '',
    storageType: '',
    startDate: '',
    endDate: '',
  });

  // ✅ Inventory by Batch: owns its own date-filter state, completely isolated from
  // Inventory by Product / Pallet. Switching tabs or changing one report never touches
  // the other reports' dates, and the batch filter is never inherited from the product
  // report (fixes shared-filter state / Issue 2).
  const [batchStartDateDraft, setBatchStartDateDraft] = useState('');
  const [batchEndDateDraft, setBatchEndDateDraft] = useState('');
  const [batchSearchDraft, setBatchSearchDraft] = useState('');
  const [batchAppliedFilters, setBatchAppliedFilters] = useState({
    companyId: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  const [detailsModal, setDetailsModal] = useState({
    open: false,
    title: '',
    fields: [],
  });
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);

  // LEGACY: auto-applied debounced filters on every date keystroke.
  // Disabled: product report dates apply via Search; company filter still applies immediately.
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setDebouncedFilters({
  //       companyId: selectedCompanyId,
  //       search: searchFilter,
  //       startDate: startDateFilter,
  //       endDate: endDateFilter,
  //     });
  //     setPage(1);
  //   }, 400);
  //   return () => clearTimeout(timer);
  // }, [selectedCompanyId, searchFilter, startDateFilter, endDateFilter]);

  const companySelectValue =
    activeReport === 'batch' ? batchAppliedFilters.companyId : appliedFilters.companyId;

  const handleCompanyChange = (companyId) => {
    if (activeReport === 'batch') {
      setBatchAppliedFilters((prev) => ({ ...prev, companyId }));
    } else {
      setAppliedFilters((prev) => ({ ...prev, companyId }));
    }
    setPage(1);
  };

  // LEGACY: manual Search button applied draft dates to appliedFilters.
  // const handleApplyDateSearch = () => {
  //   const start = startDateDraft || todayYmdIST();
  //   const end = endDateDraft || start;
  //   setStartDateDraft(start);
  //   setEndDateDraft(end);
  //   setAppliedFilters((prev) => ({
  //     ...prev,
  //     startDate: start,
  //     endDate: end,
  //   }));
  //   setPage(1);
  // };

  const applyDateFilters = useCallback((startDraft, endDraft) => {
    const start = startDraft || '';
    const end = endDraft || (startDraft ? startDraft : '');
    setAppliedFilters((prev) => {
      if (prev.startDate === start && prev.endDate === end) return prev;
      return { ...prev, startDate: start, endDate: end };
    });
    setPage(1);
  }, []);

  useEffect(() => {
    if (activeReport !== 'product' && activeReport !== 'pallet') return undefined;
    const timer = setTimeout(() => {
      applyDateFilters(startDateDraft, endDateDraft);
    }, 400);
    return () => clearTimeout(timer);
  }, [startDateDraft, endDateDraft, activeReport, applyDateFilters]);

  // ✅ Inventory by Batch: syncs ONLY its own date inputs into batchAppliedFilters.
  // This block never reads or writes product/pallet state, so the two reports stay
  // completely independent (switching tabs or changing one report never affects the other).
  useEffect(() => {
    if (activeReport !== 'batch') return undefined;
    const timer = setTimeout(() => {
      setBatchAppliedFilters((prev) => {
        const start = batchStartDateDraft || '';
        const end = batchEndDateDraft || (batchStartDateDraft ? batchStartDateDraft : '');
        if (prev.startDate === start && prev.endDate === end) return prev;
        return { ...prev, startDate: start, endDate: end };
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [batchStartDateDraft, batchEndDateDraft, activeReport]);

  useEffect(() => {
    if (activeReport !== 'batch') return undefined;
    const timer = setTimeout(() => {
      const term = batchSearchDraft.trim();
      setBatchAppliedFilters((prev) => {
        if ((prev.search || '') === term) return prev;
        return { ...prev, search: term };
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [batchSearchDraft, activeReport]);

  useEffect(() => {
    if (activeReport !== 'pallet') return undefined;
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => {
        const nextSku = skuDraft.trim();
        const nextBin = binDraft.trim();
        const nextStorage = storageTypeDraft;
        if (
          prev.sku === nextSku &&
          prev.binCode === nextBin &&
          prev.storageType === nextStorage
        ) {
          return prev;
        }
        return {
          ...prev,
          sku: nextSku,
          binCode: nextBin,
          storageType: nextStorage,
        };
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [skuDraft, binDraft, storageTypeDraft, activeReport]);

  useEffect(() => {
    setPage(1);
  }, [palletSubReport]);

  const hasActiveFilters = useMemo(() => {
    // Check the CURRENT report's own applied filter state (batchAppliedFilters for batch,
    // appliedFilters for product/pallet). This makes the "Clear" button accurate per report
    // and prevents the Clear action on one report from being driven by another report's
    // state.
    const cur = activeReport === 'batch' ? batchAppliedFilters : appliedFilters;
    return (
      (cur.companyId || '') !== '' ||
      (cur.startDate || '') !== '' ||
      (cur.endDate || '') !== '' ||
      (cur.search || '') !== '' ||
      (cur.sku || '') !== '' ||
      (cur.binCode || '') !== '' ||
      (cur.storageType || '') !== ''
    );
  }, [activeReport, appliedFilters, batchAppliedFilters]);

  const handleClearFilters = () => {
    // Warehouse/company context stays the same for all reports; only clear search/pagination
    // helpers that are shared, then clear ONLY the CURRENT report's own dates so one
    // report's filter never wipes another report's filter (Issue 2).
    setSkuDraft('');
    setBinDraft('');
    setStorageTypeDraft('');

    if (activeReport === 'product') {
      setStartDateDraft('');
      setEndDateDraft('');
      setAppliedFilters((prev) => ({
        ...prev,
        companyId: '',
        startDate: '',
        endDate: '',
        sku: '',
        binCode: '',
        storageType: '',
      }));
    } else if (activeReport === 'batch') {
      setBatchStartDateDraft('');
      setBatchEndDateDraft('');
      setBatchSearchDraft('');
      setBatchAppliedFilters({
        companyId: '',
        search: '',
        startDate: '',
        endDate: '',
      });
    } else {
      // pallet
      setStartDateDraft('');
      setEndDateDraft('');
      setAppliedFilters((prev) => ({
        ...prev,
        companyId: '',
        startDate: '',
        endDate: '',
        sku: '',
        binCode: '',
        storageType: '',
      }));
    }
    setPage(1);
  };

  const closeDetailsModal = useCallback(() => {
    setDetailsModal({ open: false, title: '', fields: [] });
  }, []);

  const openProductDetails = useCallback((row) => {
    setDetailsModal({
      open: true,
      title: 'Product Details',
      fields: buildDetailFields(row, PRODUCT_DETAIL_FIELDS),
    });
  }, []);

  const openBatchDetails = useCallback((row) => {
    setDetailsModal({
      open: true,
      title: 'Batch Details',
      fields: buildDetailFields(row, BATCH_DETAIL_FIELDS),
    });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [activeReport]);

  const reportFilters = useMemo(() => {
    const filters = {
      companyId: appliedFilters.companyId || undefined,
      search: appliedFilters.search?.trim() || undefined,
    };
    if (activeReport === 'product' || activeReport === 'pallet') {
      if (appliedFilters.startDate) filters.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) filters.endDate = appliedFilters.endDate;
    }
    if (activeReport === 'pallet') {
      if (appliedFilters.sku) filters.sku = appliedFilters.sku;
      if (palletSubReport === 'storageVisibility') {
        if (appliedFilters.binCode) filters.binCode = appliedFilters.binCode;
        if (appliedFilters.storageType) filters.storageType = appliedFilters.storageType;
      }
    }
    return filters;
  }, [appliedFilters, activeReport, palletSubReport]);

  // ✅ Batch report uses its own isolated filter state (batchAppliedFilters), not the
  // shared product/pallet `appliedFilters`. This is the key change that keeps
  // Inventory by Product and Inventory by Batch filter states independent (Issue 2).
  const batchReportFilters = useMemo(() => {
    const filters = {
      companyId: batchAppliedFilters.companyId || undefined,
      search: batchAppliedFilters.search?.trim() || undefined,
    };
    if (batchAppliedFilters.startDate) filters.startDate = batchAppliedFilters.startDate;
    if (batchAppliedFilters.endDate) filters.endDate = batchAppliedFilters.endDate;
    return filters;
  }, [batchAppliedFilters]);

  const getPalletExportParams = () => {
    const companyId = appliedFilters.companyId || undefined;
    const palletParams = {
      companyId,
      sku: appliedFilters.sku || undefined,
    };
    if (appliedFilters.startDate) palletParams.startDate = appliedFilters.startDate;
    if (appliedFilters.endDate) palletParams.endDate = appliedFilters.endDate;
    if (palletSubReport === 'storageVisibility') {
      if (appliedFilters.binCode) palletParams.binCode = appliedFilters.binCode;
      if (appliedFilters.storageType) palletParams.storageType = appliedFilters.storageType;
    }
    return palletParams;
  };

  const handleDownloadReport = async (reportType) => {
    try {
      setDownloading(true);
      let blob;
      let filename;
      const companyId =
        (reportType === 'batch'
          ? batchAppliedFilters.companyId
          : appliedFilters.companyId) || undefined;
      const dateParams = { companyId };
      const dateSource = reportType === 'batch' ? batchAppliedFilters : appliedFilters;
      if (dateSource.startDate) dateParams.startDate = dateSource.startDate;
      if (dateSource.endDate) dateParams.endDate = dateSource.endDate;
      if (reportType === 'batch' && dateSource.search) {
        dateParams.search = dateSource.search;
      }
      const today = new Date().toISOString().split('T')[0];

      switch (reportType) {
        case 'product':
          blob = await reportService.downloadInventoryReportByProduct(dateParams);
          filename = `inventory_report_by_product_${today}.xlsx`;
          break;
        case 'batch':
          blob = await reportService.downloadInventoryReportByBatch(dateParams);
          filename = `inventory_report_by_batch_${today}.xlsx`;
          break;
        case 'uom':
          blob = await reportService.downloadInventoryReportByUOM(companyId);
          filename = `inventory_report_by_uom_${today}.xlsx`;
          break;
        case 'summary':
          blob = await reportService.downloadInventorySummary(companyId);
          filename = `inventory_summary_${today}.xlsx`;
          break;
        case 'aging':
          blob = await reportService.downloadAgingReport(companyId);
          filename = `aging_report_${today}.xlsx`;
          break;
        case 'pallet': {
          const palletParams = getPalletExportParams();
          if (palletSubReport === 'storageVisibility') {
            blob = await reportService.downloadPalletStorageVisibilityReport(palletParams);
            filename = `pallet_storage_visibility_${today}.xlsx`;
          } else {
            blob = await reportService.downloadCustomerMisPalletReport(palletParams);
            filename = `customer_mis_pallet_${today}.xlsx`;
          }
          break;
        }
        default:
          throw new Error('Invalid report type');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error(error?.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const openReportPdfClientModal = () => {
    setPalletPdfClientDraft('');
    setPalletPdfClientError('');
    setPalletPdfModalOpen(true);
  };

  const closeReportPdfClientModal = () => {
    setPalletPdfModalOpen(false);
    setPalletPdfClientError('');
  };

  const runReportPdfExport = async (clientId) => {
    try {
      setDownloadingPdf(true);
      const today = new Date().toISOString().split('T')[0];
      let blob;
      let filename;

      let clientCompany = null;
      try {
        const companyRes = await companyService.getCompanyById(clientId);
        clientCompany = companyRes?.data ?? companyRes;
      } catch {
        clientCompany =
          companies.find((c) => String(c._id || c.id) === String(clientId)) || null;
      }
      if (!clientCompany) {
        toast.error('Could not load the selected client details');
        return;
      }

      let warehouse = null;
      try {
        const whRes = await warehouseService.getMe();
        warehouse = whRes?.data ?? whRes;
      } catch {
        warehouse = null;
      }

      const pdfContext = {
        clientCompany,
        warehouse,
        reportStartDate: reportResponse?.reportStartDate ?? appliedFilters.startDate ?? null,
        reportEndDate: reportResponse?.reportEndDate ?? appliedFilters.endDate ?? null,
      };

      if (activeReport === 'product') {
        const productParams = { companyId: clientId };
        if (appliedFilters.startDate) productParams.startDate = appliedFilters.startDate;
        if (appliedFilters.endDate) productParams.endDate = appliedFilters.endDate;
        const productRes = await reportService.getInventoryReportByProduct(productParams);
        filename = `inventory_by_product_${today}.pdf`;
        await exportInventoryByProductReportPdf(productRes?.data ?? [], filename, {
          ...pdfContext,
          reportStartDate: productRes?.reportStartDate ?? appliedFilters.startDate ?? null,
          reportEndDate: productRes?.reportEndDate ?? appliedFilters.endDate ?? null,
        });
      } else if (activeReport === 'batch') {
        const batchParams = {
          companyId: clientId,
          search: batchAppliedFilters.search?.trim() || undefined,
        };
        if (batchAppliedFilters.startDate) batchParams.startDate = batchAppliedFilters.startDate;
        if (batchAppliedFilters.endDate) batchParams.endDate = batchAppliedFilters.endDate;
        const { rows, reportStartDate, reportEndDate } =
          await fetchAllInventoryBatchReportRows(reportService, batchParams);
        filename = `inventory_batch_wise_${today}.pdf`;
        await exportInventoryBatchReportPdf(rows, filename, {
          ...pdfContext,
          reportStartDate: reportStartDate ?? batchAppliedFilters.startDate ?? null,
          reportEndDate: reportEndDate ?? batchAppliedFilters.endDate ?? null,
        });
      } else if (activeReport === 'pallet') {
        const palletParams = {
          ...getPalletExportParams(),
          companyId: clientId,
        };
        if (palletSubReport === 'storageVisibility') {
          blob = await reportService.downloadPalletStorageVisibilityReport(palletParams);
          filename = `pallet_storage_visibility_${today}.pdf`;
          await exportPalletStorageReportPdfFromExcelBlob(blob, filename, pdfContext);
        } else {
          blob = await reportService.downloadCustomerMisPalletReport(palletParams);
          filename = `customer_mis_pallet_${today}.pdf`;
          await exportCustomerMisPalletReportPdfFromExcelBlob(blob, filename, {
            openInNewTab: false,
          });
        }
      }

      toast.success('PDF downloaded successfully');
      closeReportPdfClientModal();
    } catch (error) {
      console.error('Error downloading pallet PDF:', error);
      toast.error(error?.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePalletPdfModalConfirm = async () => {
    if (!palletPdfClientDraft) {
      setPalletPdfClientError('Please select a client');
      return;
    }
    const belongsToWarehouse = companies.some(
      (c) => String(c._id || c.id) === String(palletPdfClientDraft)
    );
    if (!belongsToWarehouse) {
      setPalletPdfClientError('Selected client is not available for this warehouse');
      return;
    }
    setPalletPdfClientError('');
    await runReportPdfExport(palletPdfClientDraft);
  };

  const getReports = useCallback(async ({ page: pageArg, limit: limitArg, filters }) => {
    if (activeReport === 'product') {
      return reportService.getInventoryReportByProduct(filters);
    }
    const params = {
      page: pageArg,
      limit: limitArg ?? batchReportPageSize,
      ...filters,
    };
    if (activeReport === 'batch') return reportService.getInventoryReportByBatch(params);
    if (activeReport === 'aging') return reportService.getAgingReport(params);
    if (activeReport === 'pallet') {
      if (palletSubReport === 'storageVisibility') {
        return reportService.getPalletStorageVisibilityReport(params);
      }
      return reportService.getCustomerMisPalletReport(params);
    }
    return { success: true, data: [] };
  }, [activeReport, batchReportPageSize, palletSubReport]);

  // const fetchInventoryByUOM = async () => {
  //   try {
  //     setLoading(true);
  //     const companyId = selectedCompanyId || undefined;
  //     const result = await reportService.getInventoryReportByUOM(companyId);
  //     if (result && result.success && Array.isArray(result.data)) {
  //       const flattened = [];
  //       for (const group of result.data) {
  //         for (const p of group.products || []) {
  //           flattened.push({
  //             uom: group.uom,
  //             productCode: p.productCode,
  //             productDescription: p.productDescription,
  //             companyName: p.companyName || '—',
  //             qnt: p.qnt || 0,
  //             holdQty: p.holdQty || 0,
  //           });
  //         }
  //       }
  //       setUomData(flattened);
  //     } else {
  //       setUomData([]);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching inventory by UOM:', error);
  //     setUomData([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const fetchInventorySummary = async () => {
  //   try {
  //     setLoading(true);
  //     const companyId = selectedCompanyId || undefined;
  //     const result = await reportService.getInventorySummary(companyId);
  //     if (result && result.success && result.summary) {
  //       setSummaryData(result.summary);
  //     } else {
  //       setSummaryData(null);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching inventory summary:', error);
  //     setSummaryData(null);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const { data: companiesResponse } = useQuery({
    queryKey: ['reportCompanies'],
    queryFn: () => companyService.getCompanies({ isActive: 'true' }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const companies = useMemo(() => companiesResponse?.data || [], [companiesResponse]);

  useEffect(() => {
    if (!palletPdfModalOpen || !palletPdfClientDraft) return;
    const stillValid = companies.some(
      (c) => String(c._id || c.id) === String(palletPdfClientDraft)
    );
    if (!stillValid) setPalletPdfClientDraft('');
  }, [companies, palletPdfModalOpen, palletPdfClientDraft]);

  const listReportFilters =
    activeReport === 'batch' ? batchReportFilters : reportFilters;

  const {
    data: reportResponse,
    isLoading,
    isFetching,
    isError: isReportError,
    error: reportError,
  } = useQuery({
    queryKey:
      activeReport === 'product'
        ? ['reports', activeReport, reportFilters]
        : activeReport === 'aging'
          ? ['reports', activeReport, listReportFilters]
          : ['reports', activeReport, palletSubReport, page, batchReportPageSize, listReportFilters],
    queryFn: () =>
      getReports({
        page,
        limit: activeReport === 'product' ? limit : batchReportPageSize,
        filters: listReportFilters,
      }),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const loading = isLoading || isFetching;
  const productData = useMemo(
    () => (activeReport === 'product' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const batchData = useMemo(
    () => (activeReport === 'batch' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const palletData = useMemo(
    () => (activeReport === 'pallet' && Array.isArray(reportResponse?.data) ? reportResponse.data : []),
    [activeReport, reportResponse]
  );
  const expiredBatches = useMemo(
    () => (activeReport === 'aging' && Array.isArray(reportResponse?.expiredBatches) ? reportResponse.expiredBatches : []),
    [activeReport, reportResponse]
  );
  const nearExpiryBatches = useMemo(
    () => (activeReport === 'aging' && Array.isArray(reportResponse?.nearExpiryBatches) ? reportResponse.nearExpiryBatches : []),
    [activeReport, reportResponse]
  );
  const ageingSummary = useMemo(
    () => (activeReport === 'aging' ? reportResponse?.summary || null : null),
    [activeReport, reportResponse]
  );
  const totalCount = useMemo(() => {
    if (activeReport === 'aging') {
      if (reportResponse?.count != null) {
        return Number(reportResponse.count) || 0;
      }
      const s = reportResponse?.summary;
      if (s) {
        return (
          (Number(s.expiredBatchCount) || 0) +
          (Number(s.nearExpiryBatchCount) || 0) +
          (Number(s.goodBatchCount) || 0)
        );
      }
      return expiredBatches.length + nearExpiryBatches.length;
    }
    return Number(reportResponse?.count || 0);
  }, [activeReport, reportResponse, expiredBatches.length, nearExpiryBatches.length]);

  const showReportPagination =
    activeReport === 'batch' || activeReport === 'pallet';
  const hasNextPage =
    showReportPagination && totalCount > page * batchReportPageSize;

  return (
    <div
      className="w-full pl-3 pr-6 pt-2 pb-6 bg-gray-50 min-h-screen"
      style={{ minWidth: '800px', fontFamily: "'Nunito', sans-serif" }}
    >
      <div
        className="flex flex-wrap items-center justify-between gap-4 w-full"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEF1F5',
          borderRadius: 28,
          padding: '16px 24px',
          boxShadow: '0px 0px 4px 0px #959DA526',
        }}
      >
        <h1
          style={{
            fontFamily: 'Nunito',
            fontSize: 18,
            fontWeight: 600,
            color: '#161616',
            margin: 0,
          }}
        >
          Reports
        </h1>
        <div
          className="overflow-x-auto"
          style={{
            border: '1px solid #D0D5DD',
            borderRadius: 14,
            height: 52,
            display: 'flex',
            alignItems: 'center',
            padding: 6,
            gap: 6,
          }}
        >
          {REPORT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveReport(tab.id)}
              className="flex-shrink-0 whitespace-nowrap transition"
              style={{
                background: activeReport === tab.id ? 'rgba(24,135,66,0.08)' : 'transparent',
                color: activeReport === tab.id ? '#188742' : '#101828',
                fontWeight: activeReport === tab.id ? 600 : undefined,
                borderRadius: 12,
                padding: '10px 18px',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 w-full rounded-2xl p-6 border border-[#18181B0F] bg-[#fcfcfc]">
      <div
        className={REPORT_FILTER_PANEL_CLASS}
        style={REPORT_FILTER_PANEL_SHADOW}
      >
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="min-w-0 text-base font-semibold text-gray-900 lg:text-lg">
            {CARD_TITLES[activeReport] || CARD_TITLES.product}
          </h2>
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersPanelOpen((open) => !open)}
              aria-expanded={filtersPanelOpen}
              aria-controls="reports-filter-panel"
              className={`${REPORT_FILTERS_BTN_CLASS} ${
                filtersPanelOpen ? REPORT_FILTERS_BTN_ACTIVE_CLASS : ''
              }`}
            >
              Filters
              <FiChevronDown
                className={`h-4 w-4 shrink-0 transition-transform duration-300 ${
                  filtersPanelOpen ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            </button>
            <button
              type="button"
              onClick={() => handleDownloadReport(activeReport)}
              disabled={downloading || downloadingPdf || loading}
              className={REPORT_EXPORT_BTN_CLASS}
            >
              <FiDownload className="h-4 w-4 shrink-0" />
              {downloading
                ? 'Downloading...'
                : activeReport === 'pallet'
                  ? 'Export Excel'
                  : 'Export CSV'}
            </button>
            {(activeReport === 'product' ||
              activeReport === 'pallet' ||
              activeReport === 'batch') && (
              <button
                type="button"
                onClick={openReportPdfClientModal}
                disabled={downloading || downloadingPdf || loading}
                className={REPORT_EXPORT_BTN_CLASS}
              >
                <FiDownload className="h-4 w-4 shrink-0" />
                {downloadingPdf ? 'Downloading...' : 'Export PDF'}
              </button>
            )}
          </div>
        </div>

        <div
          id="reports-filter-panel"
          className={`grid min-w-0 transition-[grid-template-rows,margin-top,opacity] duration-300 ease-in-out ${
            filtersPanelOpen
              ? 'mt-4 grid-rows-[1fr] opacity-100'
              : 'pointer-events-none mt-0 grid-rows-[0fr] opacity-0'
          }`}
          aria-hidden={!filtersPanelOpen}
        >
          <div className="min-h-0 min-w-0 overflow-hidden">
            <div className={REPORT_COLLAPSIBLE_BODY_CLASS}>
              {(activeReport === 'batch' || activeReport === 'pallet') && (
                <div className="mb-4 min-w-0 max-w-full sm:max-w-md">
                  {activeReport === 'batch' ? (
                    <label className={REPORT_FIELD_LABEL_CLASS}>
                      Search by SKU/Product Code
                      <input
                        type="text"
                        value={batchSearchDraft}
                        onChange={(e) => setBatchSearchDraft(e.target.value)}
                        placeholder="Search by SKU/Product Code"
                        className={REPORT_FIELD_INPUT_CLASS}
                      />
                    </label>
                  ) : (
                    <label className={REPORT_FIELD_LABEL_CLASS}>
                      SKU
                      <input
                        type="text"
                        value={skuDraft}
                        onChange={(e) => setSkuDraft(e.target.value)}
                        placeholder="SKU"
                        className={REPORT_FIELD_INPUT_CLASS}
                      />
                    </label>
                  )}
                </div>
              )}

              <div className="grid min-w-0 grid-cols-1 gap-x-3 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {(activeReport === 'product' ||
                  activeReport === 'pallet' ||
                  activeReport === 'batch') && (
                  <>
                    <label className={REPORT_FIELD_LABEL_CLASS}>
                      From
                      <input
                        type="date"
                        value={
                          activeReport === 'batch' ? batchStartDateDraft : startDateDraft
                        }
                        onChange={(e) =>
                          activeReport === 'batch'
                            ? setBatchStartDateDraft(e.target.value)
                            : setStartDateDraft(e.target.value)
                        }
                        className={REPORT_FIELD_INPUT_CLASS}
                        title="From Date (IST)"
                      />
                    </label>
                    <label className={REPORT_FIELD_LABEL_CLASS}>
                      To
                      <input
                        type="date"
                        value={
                          activeReport === 'batch' ? batchEndDateDraft : endDateDraft
                        }
                        onChange={(e) =>
                          activeReport === 'batch'
                            ? setBatchEndDateDraft(e.target.value)
                            : setEndDateDraft(e.target.value)
                        }
                        className={REPORT_FIELD_INPUT_CLASS}
                        title="To Date (IST)"
                      />
                    </label>
                  </>
                )}

                {activeReport === 'pallet' && palletSubReport === 'storageVisibility' && (
                  <>
                    <label className={REPORT_FIELD_LABEL_CLASS}>
                      Bin
                      <input
                        type="text"
                        value={binDraft}
                        onChange={(e) => setBinDraft(e.target.value)}
                        placeholder="Bin"
                        className={REPORT_FIELD_INPUT_CLASS}
                      />
                    </label>
                    <label className={REPORT_FIELD_LABEL_CLASS}>
                      Storage Type
                      <select
                        value={storageTypeDraft}
                        onChange={(e) => setStorageTypeDraft(e.target.value)}
                        className={`${REPORT_FIELD_INPUT_CLASS} font-normal text-[#101828]`}
                      >
                        {STORAGE_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value || 'all'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                )}

                <label className={REPORT_FIELD_LABEL_CLASS}>
                  Company
                  <div className="relative w-full min-w-0">
                    <select
                      value={companySelectValue}
                      onChange={(e) => handleCompanyChange(e.target.value)}
                      className={`${REPORT_FIELD_INPUT_CLASS} truncate border-[1.5px] border-[#188742] font-semibold text-[#188742] appearance-none cursor-pointer pr-9 hover:bg-[#188742]/5 disabled:cursor-not-allowed disabled:opacity-50`}
                      title={
                        companySelectValue
                          ? companies.find(
                              (c) => String(c._id || c.id) === String(companySelectValue)
                            )?.name
                          : 'All Companies'
                      }
                    >
                      <option value="">All Companies</option>
                      {companies.map((company) => (
                        <option key={company._id || company.id} value={company._id || company.id}>
                          {company.companyCode
                            ? `${company.companyCode} - ${company.name}`
                            : company.name}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-[#188742]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="h-4 w-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </span>
                  </div>
                </label>
              </div>

              {hasActiveFilters && (
                <div className="mt-4 flex min-w-0 flex-wrap items-center gap-2 border-t border-[#EEF1F5] pt-4">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    disabled={loading}
                    className={REPORT_CLEAR_BTN_CLASS}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

        {activeReport === 'pallet' && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {PALLET_SUB_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPalletSubReport(tab.id)}
                className="rounded-[10px] px-4 py-2 text-sm transition"
                style={{
                  background: palletSubReport === tab.id ? 'rgba(24,135,66,0.08)' : '#FFFFFF',
                  color: palletSubReport === tab.id ? '#188742' : '#101828',
                  fontWeight: palletSubReport === tab.id ? 600 : 500,
                  border: '1px solid #D0D5DD',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}


        {activeReport === 'product' && (
          loading ? (
            <SkeletonTable cols={PRODUCT_COLUMNS.length} rows={8} />
          ) : (
            <Table
              reportTable
              columns={PRODUCT_COLUMNS}
              data={productData}
              loading={false}
              emptyMessage="No data available"
              onRowClick={openProductDetails}
            />
          )
        )}

        {activeReport === 'batch' && (
          loading ? (
            <SkeletonTable cols={BATCH_COLUMNS.length} rows={8} />
          ) : (
            <Table
              reportTable
              serverPaginated
              columns={BATCH_COLUMNS}
              data={batchData}
              loading={false}
              emptyMessage="No data available"
              onRowClick={openBatchDetails}
            />
          )
        )}

        {/* {activeReport === 'uom' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
  <SummaryCard
    label="Total Products Out of Stock"
    value={uomData.filter((r) => (r.qnt ?? 0) === 0).length}
    Icon={TotalProductsIcon}
    bg="#DC2626"   // red
    stroke={true}
  />

  <SummaryCard
    label="Total Products (in report)"
    value={uomData.length}
    Icon={TotalBinsIcon}
    bg="#01C36F"   // green
    stroke={true}
  />

  <SummaryCard
    label="Total UOMs"
    value={[...new Set(uomData.map((r) => r.uom))].length}
    Icon={TotalProductsIcon}
    bg="#4F46E5"   // blue/purple
    stroke={true}
  />
</div>

                <div className="mt-8">
                  <Table
                    columns={UOM_COLUMNS}
                    data={uomData}
                    loading={loading}
                    emptyMessage="No data available"
                  />
                </div>
              </>
            )}
          </>
        )} */}

        {/* {activeReport === 'summary' && (
          <>
            {loading ? (
              <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
            ) : summaryData ? (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                <SummaryCard
                  label="Total Products"
                  value={summaryData.totalProducts ?? 0}
                  icon={<RiBox3Line className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Opening Stock"
                  value={summaryData.totalOpeningStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Inward"
                  value={summaryData.totalInwardQuantity ?? 0}
                  icon={<IoTrendingDown className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
                <SummaryCard
                  label="Total Outward"
                  value={summaryData.totalOutwardQuantity ?? 0}
                  icon={<IoAnalyticsOutline className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Return"
                  value={summaryData.totalReturnQuantity ?? 0}
                  icon={<IoIosReturnLeft className="w-4 h-4" />}
                  iconBgColor="bg-blue-50"
                  iconTextColor="text-blue-700"
                />
                <SummaryCard
                  label="Total Closing Stock"
                  value={summaryData.totalClosingStock ?? 0}
                  icon={<MdInventory className="w-4 h-4" />}
                  iconBgColor="bg-emerald-50"
                  iconTextColor="text-emerald-600"
                />
              </div>
            ) : (
              <div className="py-12 text-center text-sm text-gray-500">No data available</div>
            )}
          </>
        )} */}

        {activeReport === 'aging' && (
          <div className="space-y-8">
            {loading ? (
              <>
                <SkeletonTable cols={EXPIRED_COLUMNS.length} rows={5} />
                <SkeletonTable cols={NEAR_EXPIRY_COLUMNS.length} rows={5} />
              </>
            ) : (
              <>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                  <SummaryCard
                    label="Expired Batches"
                    value={ageingSummary?.expiredBatchCount ?? expiredBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-red-50"
                    iconTextColor="text-red-600"
                  />
                  <SummaryCard
                    label="Near Expiry (30 Days)"
                    value={ageingSummary?.nearExpiryBatchCount ?? nearExpiryBatches.length}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-amber-50"
                    iconTextColor="text-amber-600"
                  />
                  <SummaryCard
                    label="Good Batches"
                    value={ageingSummary?.goodBatchCount ?? 0}
                    icon={<MdInventory className="w-4 h-4" />}
                    iconBgColor="bg-emerald-50"
                    iconTextColor="text-emerald-600"
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Expired Batches</h3>
                  <Table
                    reportTable
                    columns={EXPIRED_COLUMNS}
                    data={expiredBatches}
                    loading={loading}
                    emptyMessage="No expired batches"
                    onRowClick={openBatchDetails}
                  />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Near Expiry Batches (Next 30 Days)</h3>
                  <Table
                    reportTable
                    columns={NEAR_EXPIRY_COLUMNS}
                    data={nearExpiryBatches}
                    loading={loading}
                    emptyMessage="No near expiry batches"
                    onRowClick={openBatchDetails}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {activeReport === 'pallet' && (
          <>
            {isReportError ? (
              <div className="py-12 text-center text-sm text-red-600">
                {reportError?.message || 'Failed to load pallet report'}
              </div>
            ) : loading ? (
              <SkeletonTable
                cols={
                  palletSubReport === 'storageVisibility'
                    ? PALLET_STORAGE_COLUMNS.length
                    : PALLET_CUSTOMER_MIS_COLUMNS.length
                }
                rows={8}
              />
            ) : (
              <Table
                reportTable
                serverPaginated
                columns={
                  palletSubReport === 'storageVisibility'
                    ? PALLET_STORAGE_COLUMNS
                    : PALLET_CUSTOMER_MIS_COLUMNS
                }
                groupedHeaders={
                  palletSubReport === 'storageVisibility'
                    ? PALLET_STORAGE_GROUPED_HEADERS
                    : null
                }
                data={palletData}
                loading={false}
                emptyMessage="No pallet data available"
              />
            )}
          </>
        )}
        {showReportPagination && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page</span>
              <select
                value={batchReportPageSize}
                onChange={(e) => {
                  setBatchReportPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm font-medium text-gray-700 shadow-sm"
              >
                {[5, 10, 15, 20].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1 || loading}
                className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {Math.max(1, Math.ceil(totalCount / batchReportPageSize) || 1)} ({totalCount} total records)
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={loading || !hasNextPage}
                className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ReportDetailsModal
        open={detailsModal.open}
        onClose={closeDetailsModal}
        title={detailsModal.title}
        fields={detailsModal.fields}
      />

      <Modal
        isOpen={palletPdfModalOpen}
        title="Select Client"
        onClose={closeReportPdfClientModal}
        onConfirm={handlePalletPdfModalConfirm}
        confirmText="OK"
        cancelText="Cancel"
        confirmLoading={downloadingPdf}
      >
        <p className="text-sm text-gray-600 mb-3">
          Choose the client for this PDF export.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
        <select
          value={palletPdfClientDraft}
          onChange={(e) => {
            setPalletPdfClientDraft(e.target.value);
            if (palletPdfClientError) setPalletPdfClientError('');
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
        >
          <option value="">Select client</option>
          {companies.map((company) => (
            <option key={company._id || company.id} value={company._id || company.id}>
              {company.companyCode ? `${company.companyCode} - ${company.name}` : company.name}
            </option>
          ))}
        </select>
        {palletPdfClientError ? (
          <p className="mt-2 text-sm text-red-600" role="alert">{palletPdfClientError}</p>
        ) : null}
      </Modal>
    </div>
  );
}
